import type { Dispatch, SetStateAction } from 'react'
import * as Sentry from '@sentry/react'
import type { AIOperation } from '@bandos/shared'
import { SuggestionsService } from '../../../services'
import type { AISuggestionRecord } from '../../../services'
import type { AISuggestion, SourceNodeInfo } from '../types/aiTypes'

// ============================================================================
// DB RECORD CONVERSION
// ============================================================================

/**
 * Converts a database suggestion record to the local AISuggestion format.
 * Pure function — no React dependencies.
 */
function convertDbSuggestionToLocal(record: AISuggestionRecord): AISuggestion {
  return {
    id: `suggestion-${record.suggested_node_id}`,
    dbId: record.id,
    title: record.title,
    description: record.description ?? '',
    category: record.category,
    selected: false,
    originalNode: record.original_node,
    originalEdge: record.original_edge ?? undefined,
    batchId: record.batch_id,
    createdAt: new Date(record.created_at).getTime(),
    userFeedback: record.user_feedback ?? undefined,
  }
}

// ============================================================================
// DB LOADING
// ============================================================================

interface LoadSuggestionsCallbacks {
  setSuggestions: Dispatch<SetStateAction<AISuggestion[]>>
  setSourceNodes: Dispatch<SetStateAction<Map<string, SourceNodeInfo>>>
  setSuggestionsLoaded: Dispatch<SetStateAction<boolean>>
}

/**
 * Loads pending suggestions from the database for a given project.
 * Rebuilds the sourceNodes map from loaded records.
 */
export async function loadSuggestionsFromDB(
  projectId: string,
  callbacks: LoadSuggestionsCallbacks
): Promise<void> {
  const { setSuggestions, setSourceNodes, setSuggestionsLoaded } = callbacks

  try {
    const result = await SuggestionsService.getPendingSuggestions(projectId)
    if (result.error) {
      console.error('Failed to load suggestions:', result.error.message)
      Sentry.captureException(new Error(result.error.message), {
        tags: { feature: 'mindmap', action: 'load_suggestions' },
      })
      setSuggestionsLoaded(true)
      return
    }

    if (result.data?.suggestions) {
      const loadedSuggestions = result.data.suggestions.map(convertDbSuggestionToLocal)
      setSuggestions(loadedSuggestions)

      // Rebuild sourceNodes map from loaded suggestions
      const sourceNodeMap = new Map<string, SourceNodeInfo>()
      for (const record of result.data.suggestions) {
        const existing = sourceNodeMap.get(record.source_node_id)
        const count = (existing?.suggestionCount ?? 0) + 1

        sourceNodeMap.set(record.source_node_id, {
          node: record.original_node,
          operation: record.operation as AIOperation,
          isLoading: false,
          error: null,
          suggestionCount: count,
          lastUpdated: new Date(record.created_at).getTime(),
          viewed: true, // Loaded suggestions are considered viewed
          feedbackHistory: existing?.feedbackHistory ?? [],
        })
      }
      setSourceNodes(sourceNodeMap)
    }
    setSuggestionsLoaded(true)
  } catch (err) {
    console.error('Error loading suggestions from database:', err)
    Sentry.captureException(err, {
      tags: { feature: 'mindmap', action: 'load_suggestions' },
    })
    setSuggestionsLoaded(true)
  }
}
